import { useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Users } from "lucide-react";
import * as api from "../api/CrmApi";
import type { CrmOpportunity } from "../api/CrmApi";
import { useCrm } from "../modal/CrmContext";
import { DeadlineChip, formatDate, ArtifactDot, stageTone, GatesVerdictPill } from "./crmUi";
import StatusPill from "shared/StatusPill";
import { TONE_DOT } from "shared/tones";
import StatusKanbanBoard, { type KanbanColumn } from "shared/StatusKanbanBoard";

interface Props {
  onOpen: (id: number) => void;
  /** Bumped by the parent when something outside the board (e.g. the detail
      modal) may have changed opportunities — triggers a reload. */
  version?: number;
}

export default function KanbanBoard({ onOpen, version = 0 }: Props) {
  const { pipeline, search, gatesFilter, changeStage } = useCrm();
  const location = useLocation();
  const primeContactsBasePath = location.pathname.startsWith("/employee")
    ? "/employee/crm/prime-contacts"
    : "/admin/crm/prime-contacts";

  const fetchColumn = useCallback(async (stageKey: string, page: number, pageSize: number) => {
    if (!pipeline) return { items: [], total: 0 };
    const res = await api.listOpportunities({
      pipeline_key: pipeline.key,
      stage_key: stageKey,
      search: search.trim() || undefined,
      gates_verdict: gatesFilter || undefined,
      page,
      page_size: pageSize,
    });
    return { items: res.items, total: res.total_count };
  }, [pipeline, search, gatesFilter]);

  if (!pipeline) return null;

  const columns: KanbanColumn[] = pipeline.stages.map((stage) => ({
    key: stage.key,
    label: stage.name,
    isTerminal: stage.is_terminal,
    // Column accent dot — same tone as StageBadge in crmUi.
    dotColorClass: TONE_DOT[stageTone(stage.key)],
  }));

  return (
    <StatusKanbanBoard<CrmOpportunity>
      columns={columns}
      version={version}
      getId={(opp) => opp.id}
      getStatus={(opp) => opp.stage_key}
      withStatus={(opp, newStage) => {
        const stage = pipeline.stages.find((s) => s.key === newStage);
        return { ...opp, stage_key: newStage, stage_name: stage?.name ?? newStage };
      }}
      fetchColumnItems={fetchColumn}
      onStatusChange={(opp, newStage) => changeStage(opp.id, newStage)}
      renderCard={(opp) => (
        <div
          onClick={() => onOpen(opp.id)}
          className="rounded-xl border border-border bg-surface shadow-md p-3 cursor-grab active:cursor-grabbing hover:border-primary-border transition-colors"
        >
          <p className="text-sm text-foreground leading-snug line-clamp-2">{opp.title}</p>
          {(opp.rfp?.agency_name || opp.rfp?.solicitation_number) && (
            <p className="text-[11px] text-muted-foreground truncate mt-1">
              {[opp.rfp?.agency_name, opp.rfp?.solicitation_number].filter(Boolean).join(" · ")}
            </p>
          )}
          {opp.created_at && (
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Added: {formatDate(opp.created_at, true)}
            </p>
          )}
          {opp.rfp?.proposal_deadline && (
            <p className="text-[11px] text-destructive mt-1 [&_span]:!text-destructive [&_span_span]:!font-bold">
              Due: <DeadlineChip value={opp.rfp.proposal_deadline} />
            </p>
          )}
          {opp.rfp?.gates_verdict && (
            <div className="mt-1.5">
              <GatesVerdictPill verdict={opp.rfp.gates_verdict} size="sm" />
            </div>
          )}
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-1">
              <ArtifactDot done={!!opp.rfp?.page_built_at} label="P" name="Page — decoded RFP web page built" />
              <ArtifactDot done={!!opp.rfp?.email_built_at} label="E" name="Email — outreach emails written" />
              <ArtifactDot done={!!opp.rfp?.primes_extracted_at} label="C" name="Contacts — prime contractor contacts extracted" />
              {opp.rfp?.primes_extracted_at && (
                <Link
                  to={`${primeContactsBasePath}?opportunity_id=${opp.id}`}
                  onClick={(e) => e.stopPropagation()}
                  draggable={false}
                  title="View this RFP's prime contacts"
                  className="text-muted-foreground hover:text-primary"
                >
                  <Users className="w-3 h-3" />
                </Link>
              )}
              {(opp.rfp?.outreach_replies ?? 0) > 0 && (
                <StatusPill tone="success" size="sm">
                  {opp.rfp!.outreach_replies}↩
                </StatusPill>
              )}
            </div>
          </div>
        </div>
      )}
    />
  );
}
